<template>
  <div class="relative overflow-hidden rounded-lg">
    <!-- Mouse-responsive blob background -->
    <div 
      class="blob-container" 
      @mousemove="handleMouseMove"
      ref="containerRef"
    >
      <div class="review-blob blob-1" :style="blob1Style"></div>
      <div class="review-blob blob-2" :style="blob2Style"></div>
      <div class="review-blob blob-3" :style="blob3Style"></div>
    </div>
    
    <!-- Content overlay -->
    <div class="relative z-10  py-20">
      <div class="text-4xl p-8 text-center font-bold">
        Review<span class="text-orange-500">IQ</span>
      </div>
      <div 
        class="reactive-text-wrapper sm:w-[50%] mx-auto"
        @mousemove="handleTextMouseMove"
        @mouseenter="isTextHovering = true"
        @mouseleave="handleTextMouseLeave"
        ref="textRef"
        :style="textTransform"
      >
        <div class="text-glow" :style="textGlowPosition"></div>
        <div class="text-content bg-black bg-opacity-30 backdrop-blur-sm p-4 rounded">
          <p class="text-2xl">
            ReviewIQ is a powerful and intuitive review management platform we've
            built in-house that helps businesses collect payments and valuable
            feedback from their clients.
            <br />
            <br />
            Perfect the art of review acquisition with ReviewIQ through custom
            review pages, personalized review requests, secure payment processing,
            and insightful metrics.
            <br />
            <br />
            <span class="block text-center">
              Get started for free or request a demo to see how ReviewIQ can help you.
            </span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const containerRef = ref(null);
const mouseX = ref(0.5);
const mouseY = ref(0.5);

const handleMouseMove = (e) => {
  if (!containerRef.value) return;
  const rect = containerRef.value.getBoundingClientRect();
  mouseX.value = (e.clientX - rect.left) / rect.width;
  mouseY.value = (e.clientY - rect.top) / rect.height;
};

// Blobs subtly follow mouse with wider movement range
const blob1Style = computed(() => ({
  left: `${-10 + mouseX.value * 60}%`,
  top: `${-5 + mouseY.value * 70}%`,
}));

const blob2Style = computed(() => ({
  left: `${20 + mouseX.value * 50}%`,
  top: `${-10 + mouseY.value * 60}%`,
}));

const blob3Style = computed(() => ({
  left: `${40 + mouseX.value * 55}%`,
  top: `${30 + mouseY.value * 65}%`,
}));

// Text paragraph hover effects
const textRef = ref(null);
const isTextHovering = ref(false);
const textMouseX = ref(0.5);
const textMouseY = ref(0.5);

const handleTextMouseMove = (e) => {
  if (!textRef.value) return;
  const rect = textRef.value.getBoundingClientRect();
  textMouseX.value = (e.clientX - rect.left) / rect.width;
  textMouseY.value = (e.clientY - rect.top) / rect.height;
};

const handleTextMouseLeave = () => {
  isTextHovering.value = false;
  // Don't reset position - let it fade out where it is
};

// 3D tilt effect for text
const textTransform = computed(() => {
  if (!isTextHovering.value) return {};
  
  const tiltX = (textMouseY.value - 0.5) * -10;
  const tiltY = (textMouseX.value - 0.5) * 10;
  
  return {
    transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(1.02)`,
  };
});

// Gradient glow for text
const textGlowPosition = computed(() => ({
  background: `radial-gradient(circle at ${textMouseX.value * 100}% ${textMouseY.value * 100}%, rgba(255, 165, 0, 0.3), transparent 60%)`,
  opacity: isTextHovering.value ? 1 : 0,
}));
</script>

<style scoped>
.blob-container {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%);
  /* Feathered edges using mask */
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  ),
  linear-gradient(
    to right,
    transparent 0%,
    black 3%,
    black 97%,
    transparent 100%
  );
  mask-composite: intersect;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    black 5%,
    black 95%,
    transparent 100%
  ),
  linear-gradient(
    to right,
    transparent 0%,
    black 3%,
    black 97%,
    transparent 100%
  );
  -webkit-mask-composite: source-in;
}

.reactive-text-wrapper {
  position: relative;
  display: block;
  transition: all 0.6s cubic-bezier(0.25, 0, 0.25, 1);
  transform-style: preserve-3d;
  cursor: default;
}

.text-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  transition: opacity 1.5s ease, background 2s ease;
  z-index: 0;
  border-radius: 8px;
}

.text-content {
  position: relative;
  z-index: 1;
}

.review-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.5;
  transition: all 0.8s cubic-bezier(0.25, 0, 0.25, 1);
  animation: drift 25s cubic-bezier(0.25, 0, 0.25, 1) infinite;
  pointer-events: none;
}

.blob-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #00d4ff 0%, rgba(0, 212, 255, 0.3) 50%, transparent 70%);
  animation-delay: 0s;
}

.blob-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #4a90e2 0%, rgba(74, 144, 226, 0.3) 50%, transparent 70%);
  animation-delay: -8s;
}

.blob-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, #667eea 0%, rgba(102, 126, 234, 0.3) 50%, transparent 70%);
  animation-delay: -16s;
}

@keyframes drift {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(40px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-30px, 40px) scale(0.9);
  }
}

@media (prefers-reduced-motion: reduce) {
  .review-blob {
    animation: none;
    transition: none;
  }
}
</style>
